import _ from 'lodash';
import Immutable from 'seamless-immutable';
import moment from 'moment-timezone';
import { enums } from 'console-kit';
import RefData from 'ref-data';
import { slideNavigationControlTypes, progressItemTypes } from '../../common/enums/experienceDisplayType';
import { defaults } from '../../common/models/index.js';
import { QUILL_EMPTY_CONTAINER_HTML } from '../../common/tools';
import { getEntryStrategiesByExperienceSubtype } from '../../common/Utils.js';
import Sharing from './sharing';
import { deserializePresentationConfig, serializePresentationConfig } from './experience-utils.js';

export const defaultNavConfig = {
  navigationControlType: slideNavigationControlTypes.ARROWS,
  progressItemType:      progressItemTypes.BARS,
};

/**
 * Experience
 * @typedef {object}  Experience
 * @property {array}  availableEntryFrequencyStrategies - the entry frequency strategies available for this experience
 * @property {string} approval
 * @property {number} appVersion - the supported appVersion
 * @property {string} backgroundAssetRef - assetRef for the experience background image
 * @property {string} byline
 * @property {string} classRef
 * @property {string} containerRef
 * @property {string} createdBy - the user that created the experience
 * @property {string} customScript - the custom JavaScript
 * @property {string} customStyles - the custom CSS
 * @property {string} dateCreated - the experience creation date
 * @property {string} destinationType - the target destination type for the experience RefData.ENUM.TYPE.DESTINATION.*
 * @property {object} displayType - will this experience display as a single page or as a series of slides? RefData.Enum.DISPLAY.DISPLAY.*
 * @property {object} entryFrequencyStrategy - how often users can enter this experience RefData.Enum.STRATEGY.ENTRY.*
 * @property {object} entrySourceStrategy - how we collect data about users in this experience RefData.Enum.STRATEGY.CAMPAIGNENTRY.*
 * @property {object} entryTimeCloseMessage - message to show when entry collection is configured with time from/to limits and the experience is being accessed outside of the specified hours
 * @property {object} entryTimeFrom - if configured, collect data starting with this hour of the day
 * @property {object} entryTimeTo - if configured, collect data up to this hour of the day
 * @property {string} expiryDate - when is the experience set to export
 * @property {string} externalRef - the tracking reference. Used to identify the experience in external marketing systems
 * @property {object} formatType - macro or micro experience RefData.Enum.FORMAT.DISPLAY.*
 * @property {string} formName - experience form name
 * @property {object} fraudDetectionStrategy - what fraud prevention measures are active on the experience RefData.Enum.STRATEGY.FRAUD.*
 * @property {object} votingFraudDetectionStrategy - what voting fraud prevention measures are active on the experience RefData.Enum.STRATEGY.FRAUD.*
 * @property {object} votingStrategy - what voting option is active on the experience RefData.Enum.STRATEGY.VOTING.*
 * @property {boolean} hasCustomScript - dose the experience have custom javascript defined
 * @property {boolean} hasCustomStyles - does the experience have custom styles defined
 * @property {boolean} hasRules - true if the experience has either rulesText or rulesURL
 * @property {boolean} hasSharing - has sharing
 * @property {boolean} hasUGC - has ugc
 * @property {boolean} hasForm - has form
 * @property {string} headline - public facing experience name
 * @property {string} footerConfig - JSON string config for experience footer
 * @property {string} id - the experience id
 * @property {boolean} isAdFormat - is this an Ad experience
 * @property {boolean} isApproved - experience is in the approved state
 * @property {boolean} isDraft - experience is in the draft state
 * @property {boolean} isExpired - experience is in the expired state
 * @property {RefData} interactionStrategy - a flag for how the campaign stores its data RefData.Enum.INTERACTION.*
 * @property {boolean} interactionStrategyIdPartition - test for whether the interaction strategy is partition
 * @property {boolean} isDeprecated - is experience deprecated
 * @property {boolean} isLive - experience is published
 * @property {string} isPolicyEmpty - is there data in the policy config?
 * @property {boolean} isPopulated - whether the model contains data
 * @property {boolean} isReady - experience is in the ready state
 * @property {boolean} isRollingRetentionMode - returns true if the retention mode is rolling and false if it based on a fixed date
 * @property {boolean} isScheduled - experience is in the scheduled state
 * @property {object} judgingStrategy - the experience typeRef RefData.ENUM.STRATEGY.JUDGING.*
 * @property {object} lastPublish - last publish date of experience, if it failed and what was the error
 * @property {string} lastUpdated - the experience last update date
 * @property {string} innetworkConfig - JSON string config of social networks for experience
 * @property {string} locale - locale typeref ID
 * @property {Date} maxExpiryDate - max expiry date
 * @property {Date} maxPublishDate - max publish date, choosing between expiry date and 'a year later', whichever is closer
 * @property {?Date} maxAllowedExtensionDate - for an experience with an expiration date, this will be the last day you can extend and experience expiration to, any date after this will be prohibited. For a rolling experience this will be null... TODO This would be the (publishDate + storageRetentionPeriod - 7 days).
 * @property {?Date} maxExtensionDate - for an experience with an expiration date, this is the last day you can set your expiration date to where the data will be still be retained for the entire retention period. You can still pick a date after the maxExtensionDate (and before the maxAllowedExtensionDate), but the data will not be retained for the full retention period. For a rolling experience this will be null
 * @property {string} maximumEntrants maximum entrants allowed in the campaign
 * @property {int} maxEntryFrequencyStrategy max entry frequency strategy, to be used when entryFrequencyStrategy is set to 'hourly'
 * @property {string} moderationStrategy
 * @property {object} modifiedBy
 * @property {string} name - the internal facing experience name
 * @property {string} navConfig - JSON config for display navigation
 * @property {string} publishDate - the date the experience was published
 * @property {string} policyConfig - privacy policy config
 * @property {string} rememberMe - will the campaign remember users when they return
 * @property {?Date} retentionDate - for an experience with an expiration date, this will be the date the data is retained to. For a rolling campaign, this will be null
 * @property {string} rulesText - written text for the experiences's rules, not used with rulesURL
 * @property {string} rulesURL - full url link to the experiences's rules, not used with rulesText
 * @property {string} sharing - redundant with hasSharing
 * @property {string} sharingConfig - JSON sharing config
 * @property {string} stagingUrl - url on which users can preview the experience
 * @property {object} status - the status of the experience RefData.Enum.STATUS.CAMPAIGNOBJECT.*
 * @property {number} storagePartition - the partition used by the backend for storing data. Used to determine retention strategy
 * @property {number} storageRetentionPeriod - the number of months that the experience will retain entry data
 * @property {object} subtypeRef - the subtype of the experience RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.*
 * @property {number} themeId - the theme used by the experience
 * @property {string} timezone - the timezone of this experience(set at experience creation or clone time)
 * @property {string} trackingCode - the tracking code. Used to identify the experience in reporting systems.
 * @property {string} twitterEntryFormat - not used?
 * @property {object} typeRef - the experience typeRef RefData.ENUM.TYPE.CAMPAIGNOBJECT.*
 * @property {object} form - the experience form {id, name, containerRef}
 * @property {boolean} hasInsights - the experience has experience insights
 * @property {string} experienceMetadata - the experience metadata config if present
 * @property {string} icon - the experience icon; image used when created as template
 * @property {string} summary - the experience summary; description used when created as template
 * @property {string} validFrom - the experience valid from date - used for countdown pages
 * @property {string} validTo - the experience valid to date - used for curtain pages
 * @property {string} canBeRepublished - the experience can be republished after it has been expired
 * @property {string} headerConfig - JSON string config for experience header
 * @property {string} votingFrequency - the voting frequency of the experience RefData.Enum.FREQUENCY.VOTING.*
 * @property {object} presentationConfig - queryString saved in the campaign with some indications about some features must be used or not when presenting the campaign
 * @property {boolean} usesDefaultFlow - experience uses default flow
 * @property {string} entryStartDate - experiences entry start date
 * @property {string} entryEndDate - experiences entry end date
 * @property {string} voteStartDate - experiences voting start date
 * @property {string} voteEndDate - experiences voting end date
 * @property {string} judgeStartDate - experiences judging start date
 * @property {string} judgeEndDate - experiences judging end date
 * @property {string} announcementStrategy - experiences announcement strategy
 * @property {string} announcementDate - experiences winners announcement date
 * @property {string} extendedProperties - JSON string of object with key value pairs with extended properties
 * @property {string} defaultTags - experiences default tags
 * @property {string} collectionId - collection id
 * @property {number} maximumVotes - meximum votes available for experience
 * @property {string} emailTemplate - email template
 * @property {string} CTA2Message -  message after voting
 * @property {boolean} hasPages - experience has pages
 * @property {object} socialAuthStrategy - object that represents current social auth strategy, RefData member
 * @property {string} socialAuthDisplay - type of auth form/gate
 * @property {string} testUrl - staging url
 * @property {string} storeApplicationFriendlyId - the friendly id of the experience
 */
/**
 * Create a ExperienceModel from provided data
 */
function create(experience = {}) {
  const result = _.clone(experience);
  result.defaultTags = experience.defaultTags ? experience.defaultTags : '';
  result.classRef = _.isString(experience.classRef) ? RefData.Model.hydrate(experience.classRef) : experience.classRef;
  result.destinationType = experience.destinationType ? RefData.Model.hydrate(experience.destinationType) : null;
  result.displayType = _.isString(experience.displayType)
    ? RefData.Model.hydrate(experience.displayType)
    : experience.displayType;

  result.entryFrequencyStrategy = experience.entryFrequencyStrategy
    ? RefData.Model.hydrate(experience.entryFrequencyStrategy)
    : null;
  result.entrySourceStrategy = experience.entrySourceStrategy
    ? RefData.Model.hydrate(experience.entrySourceStrategy)
    : null;
  result.formatType = RefData.Model.hydrate(experience.formatType);
  result.fraudDetectionStrategy = experience.fraudDetectionStrategy
    ? RefData.Model.hydrate(experience.fraudDetectionStrategy)
    : null;
  result.votingFraudDetectionStrategy = experience.votingFraudDetectionStrategy
    ? RefData.Model.hydrate(experience.votingFraudDetectionStrategy)
    : RefData.Model.hydrate(RefData.Enum.STRATEGY.FRAUD.NONE);
  result.judgingStrategy = experience.judgingStrategy ? RefData.Model.hydrate(experience.judgingStrategy) : null;
  //locale ref data has the shape of "locale.en_GB" while the BE data we receive has the shape of "en_GB"
  result.locale = RefData.Model.hydrate(`locale.${experience.locale}`);
  result.status = RefData.Model.hydrate(experience.status);
  result.subtypeRef = RefData.Model.hydrate(experience.subtypeRef);
  result.typeRef = RefData.Model.hydrate(experience.typeRef);
  result.interactionStrategy = RefData.Model.hydrate(experience.interactionStrategy);

  return Immutable(result, { prototype: model });
}

/**
 * Transforms a backend experience object into a frontend ExperienceModel
 * @param {Object} experience - backend formatted experience
 * @returns {Experience}
 */
function deserialize(experience) {
  const result = defaults(_.pick(experience, _keys), _defaultProps);

  // The backend has to send back "theme" so renaming to keep with FE conventions
  result.themeId = experience.theme;
  delete result.theme;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Sharing config.
  result.sharingConfig = Sharing.Collection.deserialize(
    result.sharingConfig ? JSON.parse(result.sharingConfig) : undefined
  );

  result.innetworkConfig = result.innetworkConfig ? JSON.parse(result.innetworkConfig) : {};

  // ====================================
  // sort out the policyConfig JSON
  // ====================================
  const _policyConfig = experience.policyConfig ? JSON.parse(experience.policyConfig) : null;

  // we will set a default policy only if the one returned is NULL or deep equals the desired default policy object
  const _isPolicyEmpty = _policyConfig === null;

  const _enabled = _.get(_policyConfig, 'enabled', defaultPolicyConfig.enabled);

  const _privacy = _.get(_policyConfig, 'privacy', defaultPolicyConfig.privacy);

  const _terms = _.get(_policyConfig, 'terms', defaultPolicyConfig.terms);

  const _emptyPolicyRuleType = experience.rulesURL ? 'url' : 'text';

  /**
   * Context: Rules modal is present in the macro experiences (and next gen AppVersion < 107)
   * where user can specify rules as piece of html (text) or link somewhere (url).
   * When migration of pages for macro experience happened we migrated rules modal as well.
   * If experience was created and rules were set to the url option in UI version before
   * migration to the React, policyConfig property was not set in the db, rulesText and rulesUrl
   * properties was used, and if such experience will be used with frontend migrated to the
   * React we will render wrong option (text instead of url) cause we were fully relying on the policyConfig
   * property. And now we are checking if policyConfig missing we are derriving type
   * from rulesUrl and rulesText.
   */
  const _rules = {
    type: _isPolicyEmpty ? _emptyPolicyRuleType : _.get(_policyConfig, 'rules.type', defaultPolicyConfig.rules.type),
    text: experience.rulesText || defaultPolicyConfig.rules.text,
    url:  experience.rulesURL || defaultPolicyConfig.rules.url,
  };

  const _policy = _isPolicyEmpty
    ? defaultPolicyConfig.policy
    : _.get(_policyConfig, 'policy', defaultPolicyConfig.policy);

  const _presentationType = _.get(_policyConfig, 'presentationType', defaultPolicyConfig.presentationType);

  result.policyConfigJSON = Immutable({
    privacy:          _privacy,
    terms:            _terms,
    rules:            _rules,
    policy:           _policy,
    presentationType: _presentationType,
    enabled:          _enabled,
  });

  result.trackingCode = experience.trackingCode === null ? '' : experience.trackingCode;

  if (experience.experienceMetadata) result.experienceMetadata = JSON.parse(experience.experienceMetadata);

  result.maxEntryFrequencyStrategy = experience.maxEntryFrequencyStrategy || 1;

  //if preentationConfig exists it needs to be turned to right form. from BE all properties are strings. it turns them to correct types
  let parsedPresentationConfig = null;
  if (experience.presentationConfig) {
    parsedPresentationConfig = {};
    for (const key in experience.presentationConfig) {
      if (experience.presentationConfig.hasOwnProperty(key)) {
        //PresentationConfig property values are stringified and we need to parse them
        parsedPresentationConfig[key] = deserializePresentationConfig(experience.presentationConfig[key]);
      }
    }
  }
  result.presentationConfig = parsedPresentationConfig;

  result.navConfig = result.navConfig ? JSON.parse(result.navConfig) : defaultNavConfig;
  // NextGen experiences does not require extra JSON parsing for headerConfig
  result.headerConfig = experience.headerConfig
    ? result.formatType === RefData.Enum.FORMAT.DISPLAY.MACRO
      ? JSON.parse(result.headerConfig)
      : result.headerConfig
    : null;
  result.extendedProperties = experience.extendedProperties ? JSON.parse(experience.extendedProperties) : null;
  result.isLastPublishSuccess = experience.lastPublish ? !experience.lastPublish.failed : true;
  result.tenantId = result.tenantId ? String(result.tenantId) : null;

  return create(result);
}

/**
 * Transform a ExperienceModel into a backend theme object
 * @returns {Object}
 */
function serialize(experience) {
  const result = experience.asMutable({ deep: true });

  result.theme = experience.themeId;
  delete result.themeId;

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Sharing config.
  result.sharingConfig = result.sharingConfig
    ? JSON.stringify(Sharing.Collection.serialize(result.sharingConfig))
    : null;

  // need to break out the rulesURL and rulesText fields into their own fields in order to maintain back compat
  const _policyConfig = experience.policyConfigJSON.asMutable({ deep: true });

  const _isRulesTypeUrl = experience.policyConfigJSON.rules.type === 'url';

  const _rulesText = _isRulesTypeUrl ? '' : _.get(_policyConfig, 'rules.text', '');

  // save an empty string if the quill container it touched but empty, otherwise break out the rules text
  result.rulesText = _rulesText === QUILL_EMPTY_CONTAINER_HTML ? '' : _rulesText;
  // grab the rules url
  result.rulesURL = _.get(_policyConfig, 'rules.url', '');

  // and then remove those attributes from the _policyConfig
  if (_policyConfig && _policyConfig.rules) {
    _policyConfig.rules.text = undefined;
    _policyConfig.rules.url = undefined;
  }

  result.policyConfig = !!_policyConfig ? JSON.stringify(_policyConfig) : null;

  result.classRef = experience.classRef.id;
  //destination type prop is only used in nextgen experiences
  result.destinationType = experience.destinationType ? experience.destinationType.id : null;
  result.displayType = experience.displayType.id;
  result.navConfig = experience.navConfig ? JSON.stringify(experience.navConfig) : null;
  result.entryFrequencyStrategy = experience.entryFrequencyStrategy ? experience.entryFrequencyStrategy.id : null;
  result.entrySourceStrategy = experience.entrySourceStrategy ? experience.entrySourceStrategy.id : null;
  result.formatType = experience.formatType.id;
  result.fraudDetectionStrategy = experience.fraudDetectionStrategy ? experience.fraudDetectionStrategy.id : null;
  result.votingFraudDetectionStrategy = experience.votingFraudDetectionStrategy
    ? experience.votingFraudDetectionStrategy.id
    : null;
  result.judgingStrategy = experience.judgingStrategy ? experience.judgingStrategy.id : null;
  result.interactionStrategy = experience.interactionStrategy ? experience.interactionStrategy.id : null;
  result.locale = experience.locale.code;
  result.status = experience.status.id;
  result.subtypeRef = experience.subtypeRef.id;
  result.typeRef = experience.typeRef.id;

  if (experience.experienceMetadata) {
    result.experienceMetadata = experienceMetadataSerialize(experience.experienceMetadata);
  }
  // NextGen experiences keep headerConfig already stringified (similar to the footer config)
  result.headerConfig = result.headerConfig
    ? result.formatType === RefData.Enum.FORMAT.DISPLAY.MACRO
      ? JSON.stringify(result.headerConfig)
      : result.headerConfig
    : null;
  result.extendedProperties = result.extendedProperties ? JSON.stringify(result.extendedProperties) : null;

  //if experience.presentationConfig exists it needs to be turned to query string because it needs to be sent to BE in that form
  let presentationConfig = null;
  if (experience.presentationConfig) {
    presentationConfig = serializePresentationConfig(experience.presentationConfig);
  }
  result.presentationConfig = presentationConfig;

  if (!experience.isMicroExperience) {
    result.timezone = experience.timezone.id;
  }

  if (result.innetworkConfig) {
    result.innetworkConfig = JSON.stringify(result.innetworkConfig);
  }

  if (result.form === '') {
    result.form = { id: null };
  }

  return _.pick(result, _keys);
}

/** The ExperienceModel prototype */
const model = {
  get hasCustomScript() {
    return !!this.customScript;
  },
  get hasCustomStyles() {
    return !!this.customStyles;
  },
  get hasSharing() {
    return !!this.sharing;
  },
  get hasRules() {
    return !!this.rulesURL || !!this.rulesText;
  },
  get typeRefIcon() {
    // we don't have icons that map to all campaign typeRefs so let's take care of the ones we don't have
    const _typeRefCode = _.get(this, ['typeRef', 'code']);

    switch (_typeRefCode) {
      case 'sweepstake':
        return enums.icons.TROPHY;
      case 'signup':
        return enums.icons.FORM;
      case 'goal':
        return enums.icons.TARGET2;
      case 'reveal':
        return enums.icons.CALENDAR;
      case 'social':
        return enums.icons.QUOTE;
      default:
        return this.typeRef.code || undefined;
    }
  },
  get interactionStrategyIsPartition() {
    return this.interactionStrategy.id === RefData.Enum.INTERACTION.PARTITION;
  },
  get isApproved() {
    return this.approval === 'status.approval.approved';
  },
  get isDraft() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.DRAFT;
  },
  get isInNetwork() {
    return _.includes(
      [
        RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKETWITTER,
        RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEINSTAGRAM,
        RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKNOFORM,
        RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKENTRYFORM,
        RefData.Enum.SUBTYPE.CAMPAIGNOBJECT.SWEEPSTAKEMULTINETWORKCLAIMFORM,
      ],
      this.subtypeRef.id || this.subtypeRef
    );
  },
  get isExpired() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.EXPIRED;
  },

  get isLive() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.PUBLISHED;
  },
  get isPopulated() {
    return !!this.id;
  },
  get isReady() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.READY;
  },
  get isScheduled() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.SCHEDULED;
  },
  get isTemplate() {
    return this.status.id === RefData.Enum.STATUS.CAMPAIGNOBJECT.TEMPLATE;
  },
  get isPageMode() {
    return this.displayType.id === RefData.Enum.DISPLAY.DISPLAY.PAGE;
  },
  get isSlideMode() {
    return (
      this.displayType.id === RefData.Enum.DISPLAY.DISPLAY.SLIDE_VERTICAL ||
      this.displayType.id === RefData.Enum.DISPLAY.DISPLAY.SLIDE_HORIZONTAL
    );
  },
  get isPolicyEmpty() {
    return this.policyConfig === null;
  },

  get hasRetentionMode() {
    return !!(this.isRollingRetentionMode || this.isExpiryRetentionMode);
  },
  get showRetentionMessaging() {
    return (
      (this.interactionStrategy.id === RefData.Enum.INTERACTION.PARTITION ||
        this.interactionStrategy === RefData.Enum.INTERACTION.PARTITION) &&
      ((this.retentionLocked && (this.isLive || this.isExpired)) || this.isScheduled)
    );
  },
  get isDeprecated() {
    return this.appVersion <= 0;
  },
  get hasPhases() {
    return this.appVersion >= 50;
  },

  /**
   * returns a Date object, using the publish date + 1 year, or now + 2 years when publish date isn't defined yet
   */
  get maxExpiryDate() {
    const publishDate = !this.publishDate ? undefined : moment(new Date(this.publishDate)).toDate();
    if (publishDate) {
      //when we have a publish date, the expiry date is an additional 1 year
      return moment(publishDate).add(1, 'y').subtract(1, 'days').toDate();
    } else {
      //since the publish date can be between now and the next 1 year, we can add an additional year to the expiry date
      return moment().add(2, 'y').subtract(1, 'days').toDate();
    }
  },

  get allowPerms() {
    if (this.isInNetwork) {
      return false;
    } else {
      return (
        this.typeRef.id === RefData.Enum.TYPE.ACCOUNT.PRIVATELABEL ||
        this.typeRef.id === RefData.Enum.TYPE.ACCOUNT.SYSTEM
      );
    }
  },

  get metadataEnabled() {
    return this.typeRef.id === RefData.Enum.TYPE.CAMPAIGNOBJECT.GAME;
  },

  /**
   * returns a Date object, choosing between expiry date and 'a year later', whichever is closer
   */
  get maxPublishDate() {
    const expiryDate = !this.expiryDate ? undefined : moment(new Date(this.expiryDate)).toDate(),
      aYearLater = moment().add(1, 'y').toDate();
    if (expiryDate) {
      return expiryDate.getTime() < aYearLater.getTime() ? expiryDate : aYearLater;
    } else {
      return aYearLater;
    }
  },

  get isActivationExperience() {
    return this.classRef.id === RefData.Enum.CLASS.CAMPAIGNOBJECT.ACTIVATION;
  },

  get canShowEntries() {
    return (
      !_.includes(
        [
          RefData.Enum.TYPE.CAMPAIGNOBJECT.CONTENT,
          RefData.Enum.TYPE.CAMPAIGNOBJECT.PROMOTION,
          RefData.Enum.TYPE.CAMPAIGNOBJECT.GOAL,
        ],
        this.typeRef.id
      ) && this.classRef.id === RefData.Enum.CLASS.CAMPAIGNOBJECT.ACTIVATION
    );
  },

  get isMicroExperience() {
    return this.formatType.id === RefData.Enum.FORMAT.DISPLAY.MICRO;
  },

  get isMacroExperience() {
    return this.formatType.id === RefData.Enum.FORMAT.DISPLAY.MACRO;
  },

  get statusLabel() {
    return this.status.code === 'Published' ? 'Live' : this.status.code;
  },

  get availableEntryFrequencyStrategies() {
    return getEntryStrategiesByExperienceSubtype(this.subtypeRef.id, []);
  },

  get isPromotionCampaign() {
    return this.typeRef.id === RefData.Enum.TYPE.CAMPAIGNOBJECT.PROMOTION;
  },
};

const defaultRulesText = '';
const defaultPolicyText = 'I have read and agree to the <rules>Rules</rules>.';

export const defaultPolicyConfig = {
  privacy: {
    type: 'url',
    url:  '',
  },
  terms: {
    type: 'url',
    url:  '',
  },
  rules: {
    type: 'text',
    url:  '',
    text: defaultRulesText,
  },
  policy:           defaultPolicyText,
  presentationType: RefData.Enum.TYPE.POLICY.CHECKBOX,
  enabled:          true,
};

const backCompatTransform = (experience) => {
  const result = _.clone(experience);
  const _keysForRefData = [
    'approval',
    'destinationType',
    'displayType',
    'eligibilityStrategy',
    'entryFrequencyStrategy',
    'entrySourceStrategy',
    'formatType',
    'judgingStrategy',
    'modifiedBy',
    'status',
    'subtypeRef',
    'fraudDetectionStrategy',
    'votingFraudDetectionStrategy',
    'theme',
    'typeRef',
    'locale',
  ];

  _.each(_keysForRefData, (k) => {
    if (result[k]) result[k] = experience[k].id;
  });

  return deserialize(result);
};

const experienceMetadataSerialize = (experienceMetadata) => {
  const fieldPropertiesToPersist = ['name', 'value'];
  const fields = _.map(experienceMetadata.fields, (field) => {
    return _.pick(field, fieldPropertiesToPersist);
  });

  return JSON.stringify({
    fields,
  });
};

/**
 * An array of keys on the API Theme object that should be shared between the API requests and responses
 */
const _keys = [
  'approval',
  'appVersion',
  'backgroundAssetRef',
  'byline',
  'canBeRepublished',
  'classRef',
  'containerId',
  'containerRef',
  'createdBy',
  'customScript',
  'customStyles',
  'dateCreated',
  'deliveryTimezone',
  'destinationType',
  'displayType',
  'eligibilityStrategy',
  'entryFrequencyStrategy',
  'entrySourceStrategy',
  'entryTimeCloseMessage',
  'entryTimeFrom',
  'entryTimeTo',
  'experienceMetadata',
  'expiryDate',
  'externalRef',
  'footerConfig',
  'form',
  'formName',
  'formatType',
  'fraudDetectionStrategy',
  'hasInsights',
  'hasUGC',
  'hasForm',
  'headline',
  'icon',
  'id',
  'interactionStrategy',
  'innetworkConfig',
  'isDeprecated',
  'isExpiryRetentionMode',
  'isRollingRetentionMode',
  'judgingStrategy',
  'lastPublish',
  'lastUpdated',
  'locale',
  'maxAllowedExtensionDate',
  'maxEntryFrequencyStrategy',
  'maxExtensionDate',
  'maximumEntrants',
  'modifiedBy',
  'name',
  'navConfig',
  'policyConfig',
  'policyConfigJSON',
  'publishDate',
  'rememberMe',
  'retentionDate',
  'retentionLocked',
  'rulesText',
  'rulesURL',
  'sharing',
  'sharingConfig',
  'stagingUrl',
  'status',
  'storagePartition',
  'storagePartitionDate',
  'storageRetentionPeriod',
  'subtypeRef',
  'summary',
  'tenant',
  'tenantId',
  'theme',
  'themeId',
  'timezone',
  'trackingCode',
  'typeRef',
  'ugcStrategy',
  'validFrom',
  'validTo',
  'votingFraudDetectionStrategy',
  'votingStrategy',
  'votingFrequency',
  'headerConfig',
  'presentationConfig',
  'usesDefaultFlow',
  'entryStartDate',
  'entryEndDate',
  'voteStartDate',
  'voteEndDate',
  'judgeStartDate',
  'judgeEndDate',
  'announcementStrategy',
  'announcementDate',
  'extendedProperties',
  'defaultTags',
  'collectionId',
  'maximumVotes',
  'emailTemplate',
  'CTA2Message',
  'hasPages',
  'canBeRepublished',
  'isLastPublishSuccess',
  'socialAuthStrategy',
  'socialAuthDisplay',
  'socialAuthEnforcement',
  'innetworkConfig',
  'testUrl',
  'storeApplicationFriendlyId',
];

// List of properties which if not set, needs to have a normal default value
const _defaultProps = {
  maximumEntrants:           '',
  maxEntryFrequencyStrategy: 1,
};

export default {
  backCompatTransform,
  experienceMetadataSerialize,
  create,
  deserialize,
  serialize,
};
